import {
    HttpErrorResponse,
    HttpHeaders,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { APP_CONFIG } from 'src/app.config';
import { TrackingService } from './tracking/tracking.service';
import { CORRELATION_ID, SKIP_TRACKING, generateCorrelationId } from './tracking/tracking-tokens';
import { ApiCallEvent, TrackingHttpPayload } from './tracking/tracking.model';

/**
 * Logging interceptor (PRODSUPP-145765).
 *
 * Records every outgoing API call as an `api_call` tracking event — method,
 * url, status, duration, outcome, and the full request/response payload (no
 * redaction by design) — stamped with the correlation id carried on the
 * request context so it ties back to the originating user action.
 *
 * It skips the tracking ingest request itself (via the SKIP_TRACKING context
 * flag and a URL guard) so the audit POST can't recursively log itself.
 *
 * All work is wrapped so a tracking failure never affects the real request.
 */
export const loggingInterceptor: HttpInterceptorFn = (req, next) => {
    const tracking = inject(TrackingService);
    const config = inject(APP_CONFIG);

    // Never log the ingest call (belt-and-suspenders: context flag + URL guard).
    if (req.context.get(SKIP_TRACKING) || req.url === config.trackingIngestUrl) {
        return next(req);
    }

    if (!tracking.isEnabled()) {
        return next(req);
    }

    // Skip excluded requests — static assets / local mocks by default, or
    // whatever `trackingExcludePatterns` defines. These are not integration
    // calls and only add noise to the audit index.
    if (isExcludedRequest(req.url, config.trackingExcludePatterns)) {
        return next(req);
    }

    const correlationId = req.context.get(CORRELATION_ID) || generateCorrelationId();
    const startedAt = Date.now();

    const record = (status: number, outcome: 'success' | 'error', responseBody: unknown): void => {
        try {
            const event: Omit<
                ApiCallEvent,
                'eventType' | 'timestamp' | 'app' | 'env' | 'user' | 'sessionId' | 'contactId'
            > = {
                correlationId,
                action: extractAction(req.body, req.url),
                url: req.url,
                method: req.method,
                status,
                durationMs: Date.now() - startedAt,
                outcome,
                request: capturePayload(req.headers, req.body),
                response: { body: responseBody },
            };
            tracking.trackApiCall(event);
        } catch {
            // Tracking must never break the request pipeline.
        }
    };

    return next(req).pipe(
        tap({
            next: (httpEvent) => {
                if (httpEvent instanceof HttpResponse) {
                    record(httpEvent.status ?? 200, 'success', httpEvent.body);
                }
            },
            error: (err) => {
                if (err instanceof HttpErrorResponse) {
                    record(err.status ?? 0, 'error', err.error);
                } else {
                    record(0, 'error', String(err));
                }
            },
        })
    );
};

/**
 * Default exclusion patterns (regex strings) applied when
 * `trackingExcludePatterns` is not configured. Covers static/local assets that
 * are not integration calls: anything under an `assets/` path, and URLs ending
 * in a common static file extension (json, svg, images, fonts, css, js, ...).
 */
const DEFAULT_EXCLUDE_PATTERNS = [
    '(^|/)assets/',
    '\\.(json|svg|png|jpe?g|gif|webp|ico|css|js|map|woff2?|ttf|eot|html?)$',
];

/** Lazily-compiled regexes, keyed by the raw pattern list to avoid rebuilds. */
let compiledKey: string | null = null;
let compiledPatterns: RegExp[] = [];

function getCompiledPatterns(patterns?: string[]): RegExp[] {
    const list = patterns && patterns.length ? patterns : DEFAULT_EXCLUDE_PATTERNS;
    const key = list.join('\u0000');
    if (key !== compiledKey) {
        compiledPatterns = list
            .map((p) => {
                try {
                    return new RegExp(p, 'i');
                } catch {
                    return null; // ignore malformed patterns rather than throw
                }
            })
            .filter((r): r is RegExp => r !== null);
        compiledKey = key;
    }
    return compiledPatterns;
}

/**
 * True when the request URL matches any configured exclude pattern (matched
 * against the path, query/hash stripped). Real integration calls to API hosts
 * do not match the defaults.
 */
function isExcludedRequest(url: string, patterns?: string[]): boolean {
    if (!url) return false;
    const path = url.split(/[?#]/)[0];
    return getCompiledPatterns(patterns).some((re) => re.test(path));
}

/**
 * Resolves the `action` for an api_call event.
 *
 * Prefers the business `action` field from the request body (the
 * orchestrator-style action, e.g. `updateContactPreferencesToDC`). When the
 * body carries no usable `action`, falls back to the last path segment of the
 * URL (e.g. `.../validate-contacts` -> `validate-contacts`) so every api_call
 * still has a meaningful, queryable action rather than null. Values are kept
 * as-is (no case normalization).
 */
function extractAction(body: unknown, url: string): string | null {
    if (body && typeof body === 'object' && 'action' in (body as Record<string, unknown>)) {
        const action = (body as Record<string, unknown>)['action'];
        if (typeof action === 'string' && action.trim() !== '') {
            return action;
        }
    }
    return actionFromUrl(url);
}

/**
 * Derives an action from a URL's last non-empty path segment, ignoring query
 * string and trailing slashes. Returns null when no segment can be resolved.
 */
function actionFromUrl(url: string): string | null {
    if (!url) return null;
    try {
        // Strip query/hash, then take the last non-empty path segment.
        const path = url.split(/[?#]/)[0];
        const segments = path.split('/').filter((s) => s.length > 0);
        const last = segments[segments.length - 1];
        return last ? decodeURIComponent(last) : null;
    } catch {
        return null;
    }
}

/** Captures headers + body for a tracked payload. Full capture, no redaction. */
function capturePayload(headers: HttpHeaders, body: unknown): TrackingHttpPayload {
    const headerMap: Record<string, string> = {};
    for (const name of headers.keys()) {
        const value = headers.get(name);
        if (value !== null) {
            headerMap[name] = value;
        }
    }
    return { headers: headerMap, body };
}
